
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { format, subDays, startOfWeek, startOfMonth, startOfYear } from 'date-fns';

export interface PolicyMetrics {
  daily: number;
  weekly: number;
  monthly: number;
  yearly: number;
  endorsementPremium: number;
  dailyTotal: number;
  weeklyTotal: number;
  monthlyTotal: number;
  yearlyTotal: number;
  // Include the policy lists to match expected types
  dailyPolicies: any[];
  weeklyPolicies: any[];
  monthlyPolicies: any[];
  yearlyPolicies: any[];
  endorsementPolicies: any[];
}

export const usePolicyMetrics = (selectedDate?: Date) => {
  const [metrics, setMetrics] = useState<PolicyMetrics>({
    daily: 0,
    weekly: 0,
    monthly: 0,
    yearly: 0,
    endorsementPremium: 0,
    dailyTotal: 0,
    weeklyTotal: 0,
    monthlyTotal: 0,
    yearlyTotal: 0,
    dailyPolicies: [],
    weeklyPolicies: [],
    monthlyPolicies: [],
    yearlyPolicies: [],
    endorsementPolicies: []
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchMetrics = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const date = selectedDate || new Date();
        const formattedDate = format(date, 'yyyy-MM-dd');
        
        // Calculate date ranges
        const weekStart = format(startOfWeek(date), 'yyyy-MM-dd');
        const monthStart = format(startOfMonth(date), 'yyyy-MM-dd');
        const yearStart = format(startOfYear(date), 'yyyy-MM-dd');

        // Define select query with producer details
        const policySelect = `
          *,
          producer:profiles!policies_producer_id_fkey (
            first_name,
            last_name
          )
        `;

        // 1. Fetch policies based on sales_date for base premium calculations
        // Daily policies (base premium)
        const { data: dailyPolicies, error: dailyError } = await supabase
          .from('policies')
          .select(policySelect)
          .eq('sales_date', formattedDate);

        if (dailyError) throw new Error(`Daily metrics error: ${dailyError.message}`);

        // Weekly policies (base premium)
        const { data: weeklyPolicies, error: weeklyError } = await supabase
          .from('policies')
          .select(policySelect)
          .gte('sales_date', weekStart)
          .lte('sales_date', formattedDate);

        if (weeklyError) throw new Error(`Weekly metrics error: ${weeklyError.message}`);

        // Monthly policies (base premium)
        const { data: monthlyPolicies, error: monthlyError } = await supabase
          .from('policies')
          .select(policySelect)
          .gte('sales_date', monthStart)
          .lte('sales_date', formattedDate);

        if (monthlyError) throw new Error(`Monthly metrics error: ${monthlyError.message}`);

        // Yearly policies (base premium)
        const { data: yearlyPolicies, error: yearlyError } = await supabase
          .from('policies')
          .select(policySelect)
          .gte('sales_date', yearStart)
          .lte('sales_date', formattedDate);

        if (yearlyError) throw new Error(`Yearly metrics error: ${yearlyError.message}`);

        // 2. Fetch policies with endorsements based on endorsement_date
        // Daily endorsements
        const { data: dailyEndorsements, error: dailyEndError } = await supabase
          .from('policies')
          .select(policySelect)
          .eq('endorsement_date', formattedDate)
          .not('endorsement_premium', 'is', null);

        if (dailyEndError) throw new Error(`Daily endorsement error: ${dailyEndError.message}`);

        // Weekly endorsements
        const { data: weeklyEndorsements, error: weeklyEndError } = await supabase
          .from('policies')
          .select(policySelect)
          .gte('endorsement_date', weekStart)
          .lte('endorsement_date', formattedDate)
          .not('endorsement_premium', 'is', null);

        if (weeklyEndError) throw new Error(`Weekly endorsement error: ${weeklyEndError.message}`);

        // Monthly endorsements
        const { data: monthlyEndorsements, error: monthlyEndError } = await supabase
          .from('policies')
          .select(policySelect)
          .gte('endorsement_date', monthStart)
          .lte('endorsement_date', formattedDate)
          .not('endorsement_premium', 'is', null);

        if (monthlyEndError) throw new Error(`Monthly endorsement error: ${monthlyEndError.message}`);

        // Yearly endorsements
        const { data: yearlyEndorsements, error: yearlyEndError } = await supabase
          .from('policies')
          .select(policySelect)
          .gte('endorsement_date', yearStart)
          .lte('endorsement_date', formattedDate)
          .not('endorsement_premium', 'is', null);

        if (yearlyEndError) throw new Error(`Yearly endorsement error: ${yearlyEndError.message}`);

        // All endorsement policies (for display purposes)
        const endorsementPolicies = yearlyEndorsements || [];

        // Calculate all policy premiums properly including endorsements
        // For a policy, the base premium is premium_amount, which should include any endorsements that have affected it
        // For policies with endorsements with an endorsement_date in our timeframe, we add the endorsement_premium
        
        // Calculate totals for policies with sales_date in the period
        const dailyBasePremium = dailyPolicies.reduce((sum, policy) => {
          const premium = Number(policy.premium_amount) || 0;
          return sum + premium;
        }, 0);
        
        const weeklyBasePremium = weeklyPolicies.reduce((sum, policy) => {
          const premium = Number(policy.premium_amount) || 0;
          return sum + premium;
        }, 0);
        
        const monthlyBasePremium = monthlyPolicies.reduce((sum, policy) => {
          const premium = Number(policy.premium_amount) || 0;
          return sum + premium;
        }, 0);
        
        const yearlyBasePremium = yearlyPolicies.reduce((sum, policy) => {
          const premium = Number(policy.premium_amount) || 0;
          return sum + premium;
        }, 0);
        
        // Calculate endorsement premiums for policies with endorsement_date in the period
        // These are separate endorsements that happened in the timeframe
        const dailyEndorsementPremium = dailyEndorsements.reduce((sum, policy) => 
          sum + (Number(policy.endorsement_premium) || 0), 0);
          
        const weeklyEndorsementPremium = weeklyEndorsements.reduce((sum, policy) => 
          sum + (Number(policy.endorsement_premium) || 0), 0);
          
        const monthlyEndorsementPremium = monthlyEndorsements.reduce((sum, policy) => 
          sum + (Number(policy.endorsement_premium) || 0), 0);
          
        const yearlyEndorsementPremium = yearlyEndorsements.reduce((sum, policy) => 
          sum + (Number(policy.endorsement_premium) || 0), 0);
        
        // Calculate total premiums from sales_date + endorsement premiums from endorsement_date
        // Make sure to avoid double counting

        // We need to exclude endorsements premium for policies that are already counted
        // in the time period (where both sales_date and endorsement_date are in range)
        const dailyPolicyIds = new Set(dailyPolicies.map(p => p.id));
        const weeklyPolicyIds = new Set(weeklyPolicies.map(p => p.id));
        const monthlyPolicyIds = new Set(monthlyPolicies.map(p => p.id));
        const yearlyPolicyIds = new Set(yearlyPolicies.map(p => p.id));

        // Only count endorsements for policies that are not already counted
        const uniqueDailyEndorsementPremium = dailyEndorsements
          .filter(p => !dailyPolicyIds.has(p.id))
          .reduce((sum, p) => sum + (Number(p.endorsement_premium) || 0), 0);

        const uniqueWeeklyEndorsementPremium = weeklyEndorsements
          .filter(p => !weeklyPolicyIds.has(p.id))
          .reduce((sum, p) => sum + (Number(p.endorsement_premium) || 0), 0);

        const uniqueMonthlyEndorsementPremium = monthlyEndorsements
          .filter(p => !monthlyPolicyIds.has(p.id))
          .reduce((sum, p) => sum + (Number(p.endorsement_premium) || 0), 0);

        const uniqueYearlyEndorsementPremium = yearlyEndorsements
          .filter(p => !yearlyPolicyIds.has(p.id))
          .reduce((sum, p) => sum + (Number(p.endorsement_premium) || 0), 0);

        // Calculate correct totals
        const dailyTotal = dailyBasePremium + uniqueDailyEndorsementPremium;
        const weeklyTotal = weeklyBasePremium + uniqueWeeklyEndorsementPremium;
        const monthlyTotal = monthlyBasePremium + uniqueMonthlyEndorsementPremium;
        const yearlyTotal = yearlyBasePremium + uniqueYearlyEndorsementPremium;
        
        // Log calculations for debugging
        console.log('Premium calculations:', {
          dailyBase: dailyBasePremium,
          dailyEndorsement: dailyEndorsementPremium,
          uniqueDailyEndorsement: uniqueDailyEndorsementPremium,
          dailyTotal,
          weeklyBase: weeklyBasePremium,
          weeklyEndorsement: weeklyEndorsementPremium,
          uniqueWeeklyEndorsement: uniqueWeeklyEndorsementPremium,
          weeklyTotal,
          monthlyBase: monthlyBasePremium,
          monthlyEndorsement: monthlyEndorsementPremium,
          uniqueMonthlyEndorsement: uniqueMonthlyEndorsementPremium,
          monthlyTotal,
          yearlyBase: yearlyBasePremium,
          yearlyEndorsement: yearlyEndorsementPremium,
          uniqueYearlyEndorsement: uniqueYearlyEndorsementPremium,
          yearlyTotal
        });

        setMetrics({
          daily: dailyBasePremium,
          weekly: weeklyBasePremium,
          monthly: monthlyBasePremium,
          yearly: yearlyBasePremium,
          endorsementPremium: yearlyEndorsementPremium,
          dailyTotal,
          weeklyTotal,
          monthlyTotal,
          yearlyTotal,
          dailyPolicies,
          weeklyPolicies,
          monthlyPolicies,
          yearlyPolicies,
          endorsementPolicies
        });
      } catch (err) {
        console.error('Error fetching policy metrics:', err);
        setError(err instanceof Error ? err.message : 'Unknown error');
        
        // Use real calculated values instead of hardcoded ones
        const { data: policiesData } = await supabase
          .from('policies')
          .select('*');
        
        // Get all policies with endorsements
        const endorsementPolicies = (policiesData || []).filter(policy => 
          policy.endorsement_premium && policy.endorsement_premium > 0
        );
        
        // Calculate base premium total from all policies
        const basePremiumTotal = (policiesData || []).reduce((sum, policy) => 
          sum + (Number(policy.premium_amount) || 0), 0);
        
        // Calculate endorsement premium total from policies with endorsements
        const endorsementPremiumTotal = endorsementPolicies.reduce((sum, policy) => 
          sum + (Number(policy.endorsement_premium) || 0), 0);
        
        // Calculate total (base premium + endorsement premium)
        const totalPremium = basePremiumTotal + endorsementPremiumTotal;
        
        setMetrics({
          daily: basePremiumTotal,
          weekly: basePremiumTotal,
          monthly: basePremiumTotal,
          yearly: basePremiumTotal,
          endorsementPremium: endorsementPremiumTotal,
          dailyTotal: totalPremium,
          weeklyTotal: totalPremium,
          monthlyTotal: totalPremium,
          yearlyTotal: totalPremium,
          dailyPolicies: policiesData || [],
          weeklyPolicies: policiesData || [],
          monthlyPolicies: policiesData || [], 
          yearlyPolicies: policiesData || [],
          endorsementPolicies: endorsementPolicies
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchMetrics();
  }, [selectedDate]);

  return { metrics, isLoading, error };
};
